import React, { useState, useEffect, useRef, useCallback } from 'react';
import { StickyNote, X, Maximize2, Minimize2, Move } from 'lucide-react';
import { HistoryTodoList } from './HistoryTodoList';
import { persistProfilePref } from '../../utils/persistProfilePref';

/**
 * FloatingTodoWidget — draggable sticky-note Post-it for providers.
 * Feb 26, 2026 (initial). Mar 1, 2026 — added drag-by-header for the OPEN
 * panel AND drag for the CLOSED yellow bubble. Position synced across devices.
 *
 * The yellow page-icon bubble in the corner IS the closed-state Post-it.
 * Both the bubble and the open panel share the same {x, y} position so dragging
 * either one parks the widget anywhere on the viewport.
 *
 * Persistence:
 *   - localStorage `writeright_floating_todo_open`     — open/closed state (mirrored to server)
 *   - localStorage `writeright_floating_todo_position` — {x, y} drag-position (mirrored to server)
 */
const STORAGE_OPEN = 'writeright_floating_todo_open';
const STORAGE_POSITION = 'writeright_floating_todo_position';
const DEFAULT_POSITION = { x: 16, y: 128 };  // matches old `left-4 top-32`

// Clamp a position into the visible viewport so the widget never drifts off-screen
// after a window resize or device rotation. Used by both the bubble and the panel.
const clampToViewport = (pos, w, h) => {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  // Leave at least 40px peeking so the user can always grab it back.
  const minX = -Math.max(0, w - 40);
  const minY = 0;
  const maxX = Math.max(0, vw - 40);
  const maxY = Math.max(0, vh - 40);
  return {
    x: Math.max(minX, Math.min(pos.x, maxX)),
    y: Math.max(minY, Math.min(pos.y, maxY)),
  };
};

const loadPosition = () => {
  try {
    const raw = localStorage.getItem(STORAGE_POSITION);
    if (!raw) return DEFAULT_POSITION;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') return parsed;
  } catch (_) { /* ignore parse errors */ }
  return DEFAULT_POSITION;
};

// Drag threshold — pointer must move ≥6px before we treat it as a drag instead of a click.
// Without this, every click would be interpreted as a 0-distance drag and the bubble
// would never open via a normal tap.
const DRAG_THRESHOLD_PX = 6;

const FloatingTodoWidget = ({ effectiveRole = 'provider' }) => {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(STORAGE_OPEN) === '1'; }
    catch { return false; }
  });
  const [maximized, setMaximized] = useState(false);
  const [position, setPosition] = useState(loadPosition);
  const [dragging, setDragging] = useState(false);
  const panelRef = useRef(null);
  const bubbleRef = useRef(null);
  const dragStateRef = useRef({ active: false, dx: 0, dy: 0, startX: 0, startY: 0, moved: false });
  const persistTimerRef = useRef(null);

  // Persist open/closed state to localStorage + server.
  useEffect(() => {
    try { localStorage.setItem(STORAGE_OPEN, open ? '1' : '0'); }
    catch { /* ignore */ }
    persistProfilePref({ floating_todo_open: open });
  }, [open]);

  // Re-hydrate position from server when AuthProvider lands user prefs.
  useEffect(() => {
    const onSync = () => setPosition(loadPosition());
    window.addEventListener('writeright:profile-synced', onSync);
    return () => window.removeEventListener('writeright:profile-synced', onSync);
  }, []);

  // Clamp on window resize so the widget never escapes the viewport.
  useEffect(() => {
    const onResize = () => {
      const el = open ? panelRef.current : bubbleRef.current;
      const w = el?.offsetWidth || 48;
      const h = el?.offsetHeight || 48;
      setPosition((p) => clampToViewport(p, w, h));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open]);

  // Debounced server persist of position (400ms after last move).
  const persistPosition = useCallback((next) => {
    try { localStorage.setItem(STORAGE_POSITION, JSON.stringify(next)); }
    catch (_) { /* ignore */ }
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      persistProfilePref({ floating_todo_position: next });
    }, 400);
  }, []);

  // Shared pointer-drag helpers — re-used for the panel header AND the bubble.
  // Returns true if we treat the gesture as a drag (vs. a click).
  const startDrag = useCallback((e, elRef) => {
    const rect = elRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragStateRef.current = {
      active: true,
      dx: e.clientX - rect.left,
      dy: e.clientY - rect.top,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
    };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ }
  }, []);

  const moveDrag = useCallback((e, elRef) => {
    const s = dragStateRef.current;
    if (!s.active) return;
    const distSq = (e.clientX - s.startX) ** 2 + (e.clientY - s.startY) ** 2;
    if (!s.moved && distSq < DRAG_THRESHOLD_PX ** 2) return;
    if (!s.moved) {
      s.moved = true;
      setDragging(true);
    }
    const el = elRef.current;
    const w = el?.offsetWidth || 48;
    const h = el?.offsetHeight || 48;
    const next = clampToViewport(
      { x: e.clientX - s.dx, y: e.clientY - s.dy },
      w, h,
    );
    setPosition(next);
  }, []);

  const endDrag = useCallback((e) => {
    const s = dragStateRef.current;
    if (!s.active) return { wasDrag: false };
    s.active = false;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* ignore */ }
    if (s.moved) {
      setDragging(false);
      persistPosition(position);
    }
    return { wasDrag: s.moved };
  }, [position, persistPosition]);

  // Patients don't need a todo widget
  if (effectiveRole !== 'provider') return null;

  if (!open) {
    return (
      <button
        ref={bubbleRef}
        type="button"
        style={{ left: `${position.x}px`, top: `${position.y}px` }}
        onPointerDown={(e) => startDrag(e, bubbleRef)}
        onPointerMove={(e) => moveDrag(e, bubbleRef)}
        onPointerUp={(e) => {
          const { wasDrag } = endDrag(e);
          // Only open the panel on a true click — a drag-release should NOT toggle.
          if (!wasDrag) setOpen(true);
        }}
        onPointerCancel={endDrag}
        className={`fixed z-[55] w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400/95 to-amber-600/95 hover:from-amber-300 hover:to-amber-500 border-2 border-amber-300/60 shadow-xl hover:shadow-2xl flex items-center justify-center transition-shadow group touch-none ${
          dragging ? 'cursor-grabbing' : 'cursor-grab'
        }`}
        data-testid="floating-todo-trigger"
        data-dragging={dragging ? '1' : '0'}
        title="Open Sticky Notes — drag to move, click to open"
        aria-label="Open sticky notes (draggable)"
      >
        <StickyNote className="w-5 h-5 text-amber-950 group-hover:rotate-6 transition-transform pointer-events-none" />
        <span className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full bg-amber-200 border border-amber-400 group-hover:scale-125 transition-transform pointer-events-none" />
      </button>
    );
  }

  return (
    <div
      ref={panelRef}
      style={{ left: `${position.x}px`, top: `${position.y}px` }}
      className={`fixed z-[55] bg-card border-2 border-amber-500/40 rounded-xl shadow-2xl flex flex-col overflow-hidden ${
        dragging ? 'transition-none select-none' : 'transition-shadow'
      } ${
        maximized
          ? 'w-[min(560px,calc(100vw-2rem))] h-[min(720px,calc(100vh-10rem))]'
          : 'w-[min(380px,calc(100vw-2rem))] h-[min(520px,calc(100vh-12rem))]'
      }`}
      data-testid="floating-todo-panel"
      data-dragging={dragging ? '1' : '0'}
    >
      {/* Header — draggable handle */}
      <div
        className={`flex items-center justify-between px-3 py-2 border-b border-border bg-amber-500/10 touch-none ${
          dragging ? 'cursor-grabbing' : 'cursor-grab'
        }`}
        onPointerDown={(e) => {
          if (e.target.closest('button')) return; // resize/close buttons stay clickable
          startDrag(e, panelRef);
        }}
        onPointerMove={(e) => moveDrag(e, panelRef)}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        data-testid="floating-todo-drag-handle"
        title="Drag to move"
      >
        <div className="flex items-center gap-2 pointer-events-none">
          <Move className="w-3.5 h-3.5 text-amber-400/70" aria-hidden="true" />
          <StickyNote className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-bold text-amber-300">Sticky Notes</span>
          <span className="text-[10px] text-muted-foreground italic">drag · synced</span>
        </div>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => setMaximized((v) => !v)}
            className="p-1 text-muted-foreground hover:text-foreground hover:bg-secondary/60 rounded transition-colors"
            data-testid="floating-todo-resize"
            title={maximized ? 'Compact view' : 'Expand'}
            aria-label="Toggle size"
          >
            {maximized ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="p-1 text-muted-foreground hover:text-foreground hover:bg-secondary/60 rounded transition-colors"
            data-testid="floating-todo-close"
            title="Close (notes saved)"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Body — reuse the existing HistoryTodoList in a compact 'floating' variant */}
      <div className="flex-1 overflow-y-auto p-2 bg-card">
        <HistoryTodoList variant="floating" />
      </div>
    </div>
  );
};

export default FloatingTodoWidget;
